import { useState, useEffect } from 'react'
import { Search } from 'lucide-react'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  debounceMs?: number
}

const SearchInput = ({ 
  value, 
  onChange, 
  placeholder = "Search...", 
  className = "",
  debounceMs = 500 
}: SearchInputProps) => {
  const [isSearching, setIsSearching] = useState(false)
  const [localValue, setLocalValue] = useState(value)

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsSearching(false)
      onChange(localValue)
    }, debounceMs)

    return () => clearTimeout(timeoutId)
  }, [localValue, onChange, debounceMs])

  const handleInputChange = (newValue: string) => {
    setLocalValue(newValue)
    setIsSearching(true)
  }

  return (
    <div className={`relative w-full ${className}`} style={{ minWidth: '200px' }}>
      {isSearching ? (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center">
          <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      )}
      <input
        type="text"
        placeholder={isSearching ? "Searching..." : placeholder}
        value={localValue}
        onChange={(e) => handleInputChange(e.target.value)}
        className={`w-full px-4 py-3 pl-12 pr-12 border rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-300 ${
          isSearching 
            ? 'border-amber-300 bg-amber-50/50 placeholder-amber-400' 
            : 'border-gray-200 bg-gray-50 placeholder-gray-400'
        }`}
      />
      {isSearching && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1 h-1 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1 h-1 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchInput
